import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { translate } from 'components/Translation';
import moment, { Moment } from 'moment';
import { hideLoadingIndicator, showLoadingIndicator } from 'redux/modules/api/loadingIndicator/actions';
import { finalize } from 'rxjs/operators';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';
import AccountFilterTable, { ISelectedAccount } from 'views/common/AccountFilterTable';
import { throwError } from 'rxjs';
import { TextCell } from 'components/Cells/Cells';
import { Column, FilterValue } from 'react-table';
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker';
import { now } from 'utils/date/getDate';
import styles from 'assets/jss/material-dashboard-pro-react/views/usersServiceRequests/usersServiceRequestsStyle';
import { makeStyles } from '@material-ui/styles';
import _ from 'lodash';
import Button from 'components/CustomButtons/Button';
import { useDispatch } from 'react-redux';
import { AccountsService } from 'services/accounts/accounts';
import ServiceRequestDialog, { Status } from '../Details/ServiceRequestDialog';
import { RequestsService } from 'services/requests/requests';
import { SingleRequest } from 'utils/api/models/requests/singleRequest';
import { setToast } from 'redux/modules/toast/actions';
import { validateDateString } from 'utils/date/validateDateString';
import { MoreVert } from '@material-ui/icons';
import AcceptRequestDialog from '../Details/AcceptRequestDialog';
import RejectRequestDialog from '../Details/RejectRequestDialog';
import CallOutlinedIcon from '@material-ui/icons/CallOutlined';
import Badge from 'components/Badge/Badge';
import { BodyS } from 'components/Typography/Typography';

const useStyles = makeStyles(styles);

interface IPeriod {
	startDate: Moment | null;
	endDate: Moment | null;
}

const LAST_DAYS_NUMBER = 30;
const TARIFF_CHANGE = 'TARIFF_CHANGE';
const SIM_ACTIVATE = 'SIM_ACTIVATE';

const ListOfRequests = () => {
	const [accounts, setAccounts] = useState<SingleCorpAccount[]>([]);
	const [selectedAccount, setSelectedAccount] = useState<ISelectedAccount | null>(null);
	const [currentPage, setCurrentPage] = useState(0);
	const [skip, setSkip] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [requestsList, setRequestsList] = useState<SingleRequest[]>([]);
	const [period, setPeriod] = useState<IPeriod>({
		startDate: moment()
			.subtract(LAST_DAYS_NUMBER, 'd')
			.startOf('day'),
		endDate: moment().endOf('day'),
	});

	const [filters, setFilters] = useState<FilterValue>({
		period: ['lastDays'],
		requestorMsisdn: [],
		userReqType: [],
		status: [],
		updatedByObjectId: [],
	});
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
	const [isAcceptDialogOpen, setIsAcceptDialogOpen] = useState<boolean>(false);
	const [isRejectDialogOpen, setIsRejectDialogOpen] = useState<boolean>(false);
	const [filteredData, setFilteredData] = useState<SingleRequest[]>([]);
	const [selected, setSelected] = useState<number[]>([]);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [selectedUserRequest, setSelectedUserRequest] = useState<SingleRequest | null>(null);
	const [fetchingUserRequests, setFetchingUserRequests] = useState(false);

	const classes = useStyles();

	const dispatch = useDispatch();

	const interactionsRequestData = useMemo(() => {
		return {
			limit: pageSize,
			skip: skip,
			dateFrom: period.startDate ? period.startDate.format('YYYY-MM-DD HH:mm:ss').toString() : null,
			dateTo: period.endDate ? period.endDate.format('YYYY-MM-DD HH:mm:ss').toString() : null,
			organizationUpId: selectedAccount ? selectedAccount.id : null,
		};
	}, [skip, pageSize, period, selectedAccount]);

	const fetchUserRequests = useCallback(() => {
		if (skip === 0) setFetchingUserRequests(true);
		return RequestsService.getUserRequests(interactionsRequestData)
			.pipe(
				finalize(() => {
					if (skip === 0) setFetchingUserRequests(false);
				})
			)
			.subscribe(
				({ response }) => {
					if (skip === 0) {
						setRequestsList(response);
					} else {
						setRequestsList((_prevState) => {
							return [..._prevState, ...response];
						});
					}
				},
				(error) => {
					dispatch(
						setToast({
							error: true,
							label: translate('service.request.fetch.requests.error'),
						})
					);
					throwError(error);
				}
			);
	}, [dispatch, interactionsRequestData]);

	useEffect(() => {
		const request = fetchUserRequests();
		return () => {
			dispatch(hideLoadingIndicator());
			request.unsubscribe();
		};
	}, [dispatch, fetchUserRequests]);

	useEffect(() => {
		const subscription = AccountsService.getCorpAccountsForCurrentUser().subscribe(({ accounts }) => {
			setAccounts(accounts);
		});
		return () => {
			subscription.unsubscribe();
		};
	}, []);

	useEffect(() => {
		setRequestsList([]);
		setSkip(0);
	}, [period, selectedAccount]);

	useEffect(() => {
		setSelected([]);
		setFilteredData([...requestsList]);
	}, [selectedAccount, requestsList]);

	const filter = useCallback(
		_.debounce(() => {
			const _searchInputValue = searchInputValue.toLowerCase();
			const filtered = requestsList.filter((request) => {
				return [
					[
						filters.requestorMsisdn.length > 0,
						filters.requestorMsisdn.some((requestor: string) => request.requestorMsisdn === requestor),
					],
					[
						filters.userReqType.length > 0,
						filters.userReqType.some((type: string) => request.userReqType === type),
					],
					[filters.status.length > 0, filters.status.some((status: string) => status === request.status)],
					[
						filters.updatedByObjectId.length > 0,
						filters.updatedByObjectId.some((updatedBy: string) => updatedBy === request.updatedBy),
					],
					[
						_searchInputValue !== '',
						['requestorMsisdn', 'userReqType', 'status']
							.filter((field) => !!request[field])
							.some((field) =>
								request[field]
									.toString()
									.toLowerCase()
									.includes(_searchInputValue)
							),
					],
				]
					.filter((check) => check[0])
					.every((check) => check[1]);
			});
			setFilteredData(filtered);
		}, 100),
		[searchInputValue, filters, requestsList]
	);

	useEffect(() => {
		filter();
	}, [filter]);

	const hideDialog = () => {
		setIsDialogOpen(false);
	};

	const dateChanged = (date: string | moment.Moment, key: 'startDate' | 'endDate'): void => {
		if (typeof date === 'string') {
			if (validateDateString(date)) {
				const updatedDate = moment(date);
				if (updatedDate.isValid()) {
					setPeriod((period) => ({ ...period, [key]: updatedDate }));
				}
			}
		} else {
			setPeriod((period) => ({ ...period, [key]: date }));
		}
	};

	const prepareDate = (value: any[]) => {
		if (!value.length) {
			setPeriod({ startDate: null, endDate: moment().endOf('day') });
		} else if (value[0] === 'lastDays') {
			setPeriod({
				startDate: moment()
					.subtract(LAST_DAYS_NUMBER, 'd')
					.startOf('day'),
				endDate: moment().endOf('day'),
			});
		}
	};

	const reFetchUserRequests = () => {
		setRequestsList([]);
		fetchUserRequests();
	};

	const serviceRequestText = (singleRequest: SingleRequest) => {
		if (singleRequest.userReqType === TARIFF_CHANGE) {
			const newPlanName = singleRequest.reqParam.productsToAdd[0].name;
			return translate('service.requests.tariff.change.request', newPlanName);
		}
		if (singleRequest.userReqType === SIM_ACTIVATE) {
			return !!singleRequest.reqParam.serialNumber
				? translate('service.requests.sim.activate')
				: translate('service.requests.esim.activate');
		}
		return singleRequest.userReqType;
	};

	return (
		<div style={{ width: '100%' }}>
			<AccountFilterTable
				withoutCheckbox={true}
				onPaggingChange={(event: any) => {
					setCurrentPage(event);
					if ((event + 1) * pageSize >= requestsList.length) {
						setSkip(requestsList.length);
					}
				}}
				onPageSizeChange={(event: any) => {
					setPageSize(event);
					if ((currentPage + 1) * event >= requestsList.length) {
						setSkip(requestsList.length);
					}
				}}
				accounts={accounts}
				setAccount={(value) => setSelectedAccount(value)}
				selectedAccount={selectedAccount}
				onFilterChange={(name, value) => {
					setFilters((filters: any) => ({ ...filters, [name]: value }));
					if (name === 'period') prepareDate(value);
				}}
				filterHeader={translate('path.users.service.requests')}
				filters={[
					{
						title: translate('payments.bills.period'),
						name: 'period',
						type: 'one',
						elements: [
							{
								title: translate('numbers.usage.customPeriod'),
								value: 'custom',
								show: (
									<>
										<div className={classes.datePicker}>
											<CustomDatePicker
												timeFormat={false}
												value={period.startDate}
												onChange={(date) => dateChanged(date, 'startDate')}
												isValidDate={(currentDate) => currentDate.isSameOrBefore(now)}
												inputProps={{
													placeholder: translate('activities.details.chooseStartDate'),
												}}
											/>
										</div>
										<div className={classes.datePicker}>
											<CustomDatePicker
												timeFormat={false}
												value={period.endDate}
												onChange={(date) => dateChanged(date, 'endDate')}
												isValidDate={(currentDate) => {
													if (period.startDate) {
														return (
															period.startDate?.isBefore(currentDate, 'day') &&
															currentDate.isSameOrBefore(now)
														);
													} else {
														return false;
													}
												}}
												inputProps={{}}
											/>
										</div>
									</>
								),
							},
							{
								title: translate('last.days', LAST_DAYS_NUMBER),
								value: 'lastDays',
							},
						],
					},
					{
						title: translate('table.user.requestor'),
						name: 'requestorMsisdn',
						type: 'multiple',
						elements: [...new Set(requestsList.map((request) => request.requestorMsisdn))].map((value) => ({
							title: value,
							value,
						})),
					},
					{
						title: translate('table.type'),
						name: 'userReqType',
						type: 'multiple',
						elements: [...new Set(requestsList.map((request) => request.userReqType))].map((value) => ({
							title: value,
							value,
						})),
					},
					{
						title: translate('table.status'),
						name: 'status',
						type: 'multiple',
						elements: [...new Set(requestsList.map((request) => request.status))].map((value) => ({
							title: value,
							value,
						})),
					},
					{
						title: translate('table.confirmedBy'),
						name: 'updatedByObjectId',
						type: 'multiple',
						elements: [
							...new Set(
								requestsList
									.filter((request) => request.updatedByObjectId !== null)
									.map((request) => request.updatedByObjectId)
							),
						].map((value) => ({
							title: value,
							value,
						})),
					},
				]}
				filtersValue={filters}
				setSearchInputValue={(value) => setSearchInputValue(value)}
				searchInputValue={searchInputValue}
				data={filteredData}
				onSelect={(value: any) => setSelected(value)}
				selectPanel={{
					selectedElements: selected.length,
				}}
				loading={fetchingUserRequests}
				selected={selected}
				columns={
					[
						{
							Header: translate('date'),
							// eslint-disable-next-line react/prop-types
							Cell: ({ original }: { original: SingleRequest }) => (
								<TextCell
									text={moment(original.createdAt)
										.format('lll')
										.toString()}
								/>
							),
							accessor: 'created',
							width: 200,
						},
						{
							Header: translate('number'),
							Cell: ({ original }: { original: SingleRequest }) => (
								<>
									<CallOutlinedIcon className={classes.numberCallIcon} />
									<TextCell text={original.requestorMsisdn} />
								</>
							),
							accessor: 'requestor',
							width: 200,
						},
						{
							Header: translate('service.requests.request'),
							Cell: ({ original }: { original: SingleRequest }) => {
								return <TextCell text={serviceRequestText(original)} />;
							},
							accessor: 'type',
						},
						{
							Header: translate('table.status'),
							Cell: ({ original }: { original: SingleRequest }) => {
								var color: types.theme.badgeColor = 'gray';
								switch (original.status) {
									case Status.ACCEPTED:
										color = 'success';
										break;
									case Status.REJECTED:
										color = 'danger';
										break;
								}
								return (
									<Badge color={color}>
										<BodyS color="secondary-light" disableMargin>
											{translate('service.requests.tariff.change.' + original.status)}
										</BodyS>
									</Badge>
								);
							},
							accessor: 'status',
							width: 150,
						},
						{
							Header: translate('table.confirmedBy'),
							Cell: ({ original }: { original: SingleRequest }) => {
								return <TextCell text={original.updatedByObjectId} />;
							},
							accessor: 'updatedByObjectId',
							width: 150,
						},
						{
							Cell: ({ original }: { original: SingleRequest }) => {
								return original.status !== Status.REJECTED && original.status !== Status.ACCEPTED ? (
									<Button
										color="primary-light"
										onClick={() => {
											setSelectedUserRequest(original);
											setIsAcceptDialogOpen(true);
										}}
									>
										{translate('service.requests.accept')}
									</Button>
								) : (
										<></>
									);
							},
							width: 100,
						},
						{
							Cell: ({ original }: { original: SingleRequest }) => {
								return original.status !== Status.REJECTED && original.status !== Status.ACCEPTED ? (
									<Button
										color="secondary-light"
										onClick={() => {
											setSelectedUserRequest(original);
											setIsRejectDialogOpen(true);
										}}
									>
										{translate('service.requests.reject')}
									</Button>
								) : (
										<></>
									);
							},
							width: 100,
						},
						{
							Cell: ({ original }: { original: SingleRequest }) => (
								<MoreVert
									onClick={() => {
										setSelectedUserRequest(original);
										setIsDialogOpen(true);
									}}
								/>
							),
							width: 50,
						},
					].filter(Boolean) as Column<any>[]
				}
			/>
			<ServiceRequestDialog
				userRequest={selectedUserRequest}
				isOpen={isDialogOpen}
				turnOffDialog={hideDialog}
				refetchUserRequests={reFetchUserRequests}
			/>
			<AcceptRequestDialog
				userRequest={selectedUserRequest}
				isOpen={isAcceptDialogOpen}
				turnOffDialog={() => setIsAcceptDialogOpen(false)}
				refetchUserRequests={reFetchUserRequests}
			/>
			<RejectRequestDialog
				userRequest={selectedUserRequest}
				isOpen={isRejectDialogOpen}
				turnOffDialog={() => setIsRejectDialogOpen(false)}
				refetchUserRequests={reFetchUserRequests}
			/>
		</div>
	);
};

export default ListOfRequests;
